import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Clock } from 'lucide-react';
import { cn } from '../../lib/cn.js';
/** Under five minutes is the warning, under one is critical. */
export const COUNTDOWN_WARNING_SECONDS = 5 * 60;
export const COUNTDOWN_CRITICAL_SECONDS = 60;
/**
 * The tone for a number of seconds, with the product's thresholds.
 *
 * Here rather than in the screen that uses it so that "when does a practice
 * start looking urgent" is one answer, and so a story can show all three
 * without restating them.
 *
 * The thresholds are absolute rather than a fraction of the duration on
 * purpose: five minutes left is five minutes left, and a student in the last
 * minute of a ten-minute practice is in the same position as one in the last
 * minute of an hour-long one.
 */
export function countdownTone(seconds) {
    if (seconds <= 0)
        return 'expired';
    if (seconds <= COUNTDOWN_CRITICAL_SECONDS)
        return 'critical';
    if (seconds <= COUNTDOWN_WARNING_SECONDS)
        return 'warning';
    return 'normal';
}
/**
 * The three colour pairs, borrowed from the ones the badges already settled
 * rather than invented here. Nothing in this component says a state with
 * colour alone: the clock glyph is always present, the digits are the content,
 * and `label` is the sentence a screen reader gets.
 */
const TONE_CLASSES = {
    normal: 'bg-surface-secondary border-border-strong text-text-strong',
    warning: 'bg-surface-secondary border-warning text-warning-text',
    critical: 'bg-error-light border-error-border text-error-text',
    expired: 'bg-error-light border-error-border text-error-text',
};
/**
 * Seconds as a clock. `5:04` under an hour, `1:05:04` over it.
 *
 * Not localized, and that is deliberate: digits separated by colons are how a
 * clock reads in both of the product's languages, and the sentence a screen
 * reader hears — which does have words in it — is `label`, written by the
 * caller from the dictionary.
 */
export function formatCountdown(seconds) {
    const safe = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const rest = safe % 60;
    const padded = `${String(minutes).padStart(hours > 0 ? 2 : 1, '0')}:${String(rest).padStart(2, '0')}`;
    return hours > 0 ? `${hours}:${padded}` : padded;
}
/**
 * The countdown on a timed practice (handoff, Molecules).
 *
 * Presentation only. It is handed a number of seconds and renders it; it owns
 * no timer, no deadline and no idea what time it is. The screen above it
 * reconciles with the server, which is what keeps "the server is the clock"
 * true of a component that draws one.
 */
export function Countdown({ seconds, label, expiredLabel, tone, className }) {
    const resolved = tone ?? countdownTone(seconds);
    const expired = resolved === 'expired';
    return (_jsxs("span", { 
        // `text` rather than `timer`: the value is already in the accessible
        // name, and a `timer` role invites a reader to poll it.
        role: "status", "aria-label": label, className: cn('inline-flex items-center gap-2 rounded-full border px-4 py-1.5', 'text-body font-semibold tabular-nums', TONE_CLASSES[resolved], className), children: [_jsx(Clock, { className: "size-icon-sm shrink-0", "aria-hidden": "true" }), _jsx("span", { "aria-hidden": "true", children: expired && expiredLabel ? expiredLabel : formatCountdown(seconds) })] }));
}
//# sourceMappingURL=Countdown.js.map